import React, { useState, useMemo, useEffect } from 'react';
import { PlusCircle, Map as MapIcon, Filter, X, SearchX, FolderOpen, XCircle, LayoutList, Columns } from 'lucide-react';

// --- IMPORTAÇÃO DE COMPONENTES E CONSTANTES ---
import ProjectStatusDisplay from '../projects/ProjectStatusDisplay';
import ProjectCard from '../common/cards/ProjectCard';
import KanbanBoard from '../projects/KanbanBoard';
import Pagination from '../common/Pagination';
import SearchInput from '../common/SearchInput';
import { PROJECT_STATUSES } from '../../constants/appConstants';
import ProjectsMapModal from '../modals/ProjectsMapModal';

// --- FUNÇÃO AUXILIAR PARA NORMALIZAÇÃO DE TEXTO ---
const normalizeText = (text) => {
    if (!text || typeof text !== 'string') return '';
    return text
        .toLowerCase()
        .normalize("NFD")
        .replace(/[\u0300-\u036f]/g, "");
};

/**
 * @description Componente de página para exibir e gerenciar a lista de projetos.
 */
const ProjectsPage = ({ projects, projectStatusChartData, userData, onOpenProjectModal, onUpdateProjectStatus, theme, darkMapEnabled }) => {
    // --- ESTADOS LOCAIS DA PÁGINA ---
    const [isMapModalOpen, setMapModalOpen] = useState(false);
    const [searchTerm, setSearchTerm] = useState('');
    const [statusFilter, setStatusFilter] = useState('Todos');
    const [isFilterOpen, setFilterOpen] = useState(false);
    const [currentPage, setCurrentPage] = useState(1);
    // 'lista' | 'kanban'
    const [viewMode, setViewMode] = useState('lista');
    const PROJECTS_PER_PAGE = 15;

    // Optimize: Pre-calculate normalized search strings to avoid repeating expensive string operations during filtering
    const preparedProjects = useMemo(() => {
        const normCache = new Map();
        const getNormalized = (text) => {
            if (!text || typeof text !== 'string') return '';
            if (normCache.has(text)) return normCache.get(text);
            const normalized = text.toLowerCase().normalize("NFD").replace(/[\u0300-\u036f]/g, "");
            normCache.set(text, normalized);
            return normalized;
        };

        return projects.map(project => {
            // ⚡ Bolt: Pre-calculate date timestamps here (only runs when 'projects' array changes)
            // instead of doing it on every keystroke in the 'filteredProjects' useMemo block.
            let sortTime = 0;
            let hasDate = false;

            if (project.prazoVencimento) {
                const d = new Date(project.prazoVencimento);
                if (!isNaN(d.getTime())) {
                    sortTime = d.getTime();
                    hasDate = true;
                }
            }

            return {
                original: project,
                sortTime,
                hasDate,
                // Concatenate all searchable fields with a distinct separator to allow single-pass checking
                searchString: `
                    ${getNormalized(project.protocolo)} |||
                    ${getNormalized(project.cidade)} |||
                    ${getNormalized(project.local)} |||
                    ${project.comments?.map(c => getNormalized(c.text)).join(' ||| ') || ''}
                `.toLowerCase()
            };
        });
    }, [projects]);

    // --- MEMOIZAÇÃO PARA FILTRAGEM E ORDENAÇÃO ---
    const filteredProjects = useMemo(() => {
        const normalizedSearch = normalizeText(searchTerm);

        // Primeiro, filtra os projetos com base no termo de busca e filtro de status
        const filtered = preparedProjects.filter(({ original: project, searchString }) => {
            const matchesStatus = statusFilter === 'Todos' || project.status === statusFilter;
            if (!matchesStatus) return false;

            if (!normalizedSearch) return true;

            // Check the pre-calculated search string
            return searchString.includes(normalizedSearch);
        });

        // Em seguida, ordena os projetos usando Schwartzian transform para evitar criações repetidas de Date
        const nowTime = new Date().getTime();

        const mapped = filtered.map(({ original: project, sortTime, hasDate }, index) => {
            const isApproved = project.status === 'Aprovado';

            return {
                project,
                index,
                isApproved,
                sortTime,
                hasDate,
                diff: hasDate ? sortTime - nowTime : 0
            };
        });

        mapped.sort((a, b) => {
            // Prioriza projetos não aprovados
            if (!a.isApproved && b.isApproved) return -1;
            if (a.isApproved && !b.isApproved) return 1;

            // Se ambos não aprovados
            if (!a.isApproved && !b.isApproved) {
                // Se um tem data e outro não
                if (!a.hasDate && b.hasDate) return 1; // Sem data vai pro fim
                if (a.hasDate && !b.hasDate) return -1;

                // Se ambos sem data, alfabético
                if (!a.hasDate && !b.hasDate) {
                     return (a.project.name || '').localeCompare(b.project.name || '');
                }

                // Ambos tem data
                // Vencidos primeiro (diff negativo)
                if (a.diff < 0 && b.diff >= 0) return -1;
                if (a.diff >= 0 && b.diff < 0) return 1;

                // Se ambos vencidos ou ambos a vencer -> Menor diff primeiro
                return a.diff - b.diff;
            }

            // Se ambos são aprovados, ordena alfabeticamente pelo nome
            return (a.project.name || '').localeCompare(b.project.name || '');
        });

        return mapped.map(item => item.project);
    }, [preparedProjects, searchTerm, statusFilter]);

    // --- LÓGICA DE PAGINAÇÃO ---
    const totalPages = Math.ceil(filteredProjects.length / PROJECTS_PER_PAGE);

    const paginatedProjects = useMemo(() => {
        const startIndex = (currentPage - 1) * PROJECTS_PER_PAGE;
        const endIndex = startIndex + PROJECTS_PER_PAGE;
        return filteredProjects.slice(startIndex, endIndex);
    }, [currentPage, filteredProjects]);

    // --- EFEITO PARA RESETAR A PÁGINA ---
    useEffect(() => {
        setCurrentPage(1);
    }, [searchTerm, statusFilter]);

    const canCreateProject = userData?.role === 'administrador';

    return (
        <div className="p-4">
            {/* Cabeçalho e Controles de Filtro */}
            <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center mb-4 gap-4">
                {/* --- TÍTULO ATUALIZADO COM A CONTAGEM TOTAL DE PROJETOS --- */}
                <h2 className="text-2xl font-bold text-slate-700 dark:text-slate-200">
                    Gestão de Projetos <span className="text-slate-400 font-medium">({projects.length})</span>
                </h2>
                <div className="flex flex-wrap items-center gap-2 w-full sm:w-auto sm:flex-nowrap">
                    <button
                        onClick={() => setMapModalOpen(true)}
                        className="btn-secondary flex items-center shrink-0 h-10 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-slate-400 focus-visible:ring-offset-2"
                        title="Abrir mapa geral de postes"
                        aria-label="Abrir mapa geral de postes"
                    >
                        <MapIcon className="h-5 w-5" />
                    </button>
                    <div className="flex-grow w-full sm:w-auto">
                        <SearchInput
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                            placeholder="Buscar por protocolo, cidade, local..."
                            className="w-full sm:w-64 md:w-80"
                        />
                    </div>

                    {isFilterOpen ? (
                        <div className="flex items-center gap-2">
                            <select
                                value={statusFilter}
                                onChange={(e) => setStatusFilter(e.target.value)}
                                className="input-style px-4"
                            >
                                <option value="Todos">Todos os Status</option>
                                {PROJECT_STATUSES.map(s => (
                                    <option key={s.value} value={s.value}>{s.label}</option>
                                ))}
                            </select>
                            <button onClick={() => setFilterOpen(false)} className="btn-secondary h-10 w-10 p-0 flex items-center justify-center focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-slate-400 focus-visible:ring-offset-2" title="Fechar filtros" aria-label="Fechar filtros">
                                <X className="h-5 w-5 shrink-0" />
                            </button>
                        </div>
                    ) : (
                        <button onClick={() => setFilterOpen(true)} className="btn-secondary h-10 w-10 p-0 flex items-center justify-center focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-slate-400 focus-visible:ring-offset-2" title="Filtrar por status" aria-label="Filtrar por status">
                            <Filter className="h-5 w-5 shrink-0" />
                        </button>
                    )}

                    {/* --- BOTÃO DE ALTERNÂNCIA DE VISUALIZAÇÃO --- */}
                    <div
                        className="flex items-center rounded-lg border border-slate-200 dark:border-slate-700 overflow-hidden shrink-0"
                        role="group"
                        aria-label="Modo de visualização"
                    >
                        <button
                            id="btn-view-lista"
                            onClick={() => setViewMode('lista')}
                            title="Visualização em lista"
                            aria-label="Visualização em lista"
                            aria-pressed={viewMode === 'lista'}
                            className={`h-10 w-10 flex items-center justify-center transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 ${
                                viewMode === 'lista'
                                    ? 'bg-blue-500 text-white'
                                    : 'bg-white dark:bg-slate-800 text-slate-500 dark:text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-700'
                            }`}
                        >
                            <LayoutList className="h-5 w-5" />
                        </button>
                        <button
                            id="btn-view-kanban"
                            onClick={() => setViewMode('kanban')}
                            title="Visualização Kanban"
                            aria-label="Visualização Kanban"
                            aria-pressed={viewMode === 'kanban'}
                            className={`h-10 w-10 flex items-center justify-center transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 ${
                                viewMode === 'kanban'
                                    ? 'bg-blue-500 text-white'
                                    : 'bg-white dark:bg-slate-800 text-slate-500 dark:text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-700'
                            }`}
                        >
                            <Columns className="h-5 w-5" />
                        </button>
                    </div>

                    {canCreateProject && (
                        <button
                            onClick={() => onOpenProjectModal()}
                            className="btn-primary flex items-center shrink-0"
                        >
                            <PlusCircle className="mr-2 h-5 w-5" /> Novo
                        </button>
                    )}
                </div>
            </div>

            {/* Gráfico de Status dos Projetos */}
            <div className="mb-6">
                <ProjectStatusDisplay data={projectStatusChartData} />
            </div>

            {/* Estado vazio compartilhado entre os dois modos */}
            {filteredProjects.length === 0 && (
                <div className="flex flex-col items-center justify-center py-12 text-center" role="status" aria-live="polite">
                    {(searchTerm || statusFilter !== 'Todos') ? (
                        <>
                            <div className="bg-slate-100 dark:bg-slate-800 p-4 rounded-full mb-4">
                                <SearchX className="h-8 w-8 text-slate-400 dark:text-slate-500" />
                            </div>
                            <h3 className="text-lg font-medium text-slate-900 dark:text-slate-100 mb-1">
                                Nenhum projeto encontrado
                            </h3>
                            <p className="text-slate-500 dark:text-slate-400 mb-6 max-w-xs mx-auto">
                                Não encontramos projetos com os filtros atuais. Tente ajustar sua busca.
                            </p>
                            <button
                                onClick={() => {
                                    setSearchTerm('');
                                    setStatusFilter('Todos');
                                }}
                                className="btn-secondary flex items-center gap-2"
                            >
                                <XCircle className="h-4 w-4" />
                                Limpar filtros
                            </button>
                        </>
                    ) : (
                        <>
                            <div className="bg-slate-100 dark:bg-slate-800 p-4 rounded-full mb-4">
                                <FolderOpen className="h-8 w-8 text-slate-400 dark:text-slate-500" />
                            </div>
                            <h3 className="text-lg font-medium text-slate-900 dark:text-slate-100 mb-1">
                                Nenhum projeto cadastrado
                            </h3>
                            <p className="text-slate-500 dark:text-slate-400 mb-6 max-w-xs mx-auto">
                                Comece criando um novo projeto para gerenciar suas obras e redes.
                            </p>
                            {canCreateProject && (
                                <button
                                    onClick={() => onOpenProjectModal()}
                                    className="btn-primary flex items-center gap-2"
                                >
                                    <PlusCircle className="h-4 w-4" />
                                    Criar Novo Projeto
                                </button>
                            )}
                        </>
                    )}
                </div>
            )}

            {/* Modo Lista: cards paginados */}
            {viewMode === 'lista' && filteredProjects.length > 0 && (
                <div className="space-y-4">
                    {paginatedProjects.map(project => (
                        <ProjectCard
                            key={project.id}
                            project={project}
                            onCardClick={onOpenProjectModal}
                        />
                    ))}
                </div>
            )}

            {/* Modo Kanban: quadro com colunas por status */}
            {viewMode === 'kanban' && filteredProjects.length > 0 && (
                <KanbanBoard
                    projects={filteredProjects}
                    onCardClick={onOpenProjectModal}
                    onUpdateStatus={onUpdateProjectStatus}
                />
            )}

            {/* Controles de Paginação — exibidos apenas no modo lista */}
            {viewMode === 'lista' && (
                <Pagination
                    currentPage={currentPage}
                    totalPages={totalPages}
                    onPageChange={setCurrentPage}
                />
            )}

            <ProjectsMapModal
                isOpen={isMapModalOpen}
                onClose={() => setMapModalOpen(false)}
                projects={projects}
                theme={theme}
                darkMapEnabled={darkMapEnabled}
            />
        </div>
    );
};

export default ProjectsPage;
